import React, { useCallback, useEffect, useMemo } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { formatPercentage } from '@chilecompra/react-kit'
import { DataTable, Select, Switch, Tab, Tabs, useSelect, useSwitch } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import {
  onGetRegionalNationalLevelThunk,
  onGetRegionalProvidersSizeThunk
} from './RegionalAmountsTradedSection.actions'
import DonutsChart from '../../components/Charts/DonutsChart'
import PieChart from '../../components/Charts/PieChart'
import RegionalAmountsTradedNationalLevel from '../../components/Infographics/RegionalAmountsTradedNationalLevel'
import RegionalAmountsTradedProvidersSize from '../../components/Infographics/RegionalAmountsTradedProvidersSize'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Description,
  GridContainer,
  GridItem,
  Info,
  Root,
  Strong,
  Title,
  Total
} from './RegionalAmountsTradedSection.styles'

/**
 * The RegionalAmountsTradedSection's container.
 */
const RegionalAmountsTradedSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { endYear, latestUpdate, loading, startYear } = useSelector(state => state.topNavbar)
  const { id: regionId, name: regionName } = useSelector(state => state.regionalSection)
  const { nationalLevel, providersSize } = useSelector(state => state.regionalAmountsTradedSection)
  const { value: yearNationalLevelValue, onChange: handleChangeNationalLevelYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => dispatch(onGetRegionalNationalLevelThunk({ regionId, year: updatedValue })),
    overwriteCallback: () => endYear
  })
  const { value: yearProvidersSizeValue, onChange: handleChangeProvidersSizeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => dispatch(onGetRegionalProvidersSizeThunk({ regionId, year: updatedValue })),
    overwriteCallback: () => endYear
  })
  const { value: currencyNationalLevelValue, onChange: handleChangeNationalLevelCurrency } = useSwitch()
  const { value: currencyProvidersSizeValue, onChange: handleChangeProvidersSizeCurrency } = useSwitch()
  const {
    disableCaptureImage: disableCaptureNationalLevelImage,
    disableDownloadFile: disableDownloadNationalLevelFile,
    ref: nationalLevelDataLayoutRef,
    onCaptureImage: handleCaptureNationalLevelImage,
    onChangeView: handleChangeNationalLevelView,
    onDownloadFile: handleDownloadNationalLevelFile,
    update: updateNationalLevelDataLayout
  } = useDataLayout({
    dependencies: [nationalLevel.csv, yearNationalLevelValue],
    captureImageCallback: () =>
      exportToImage(
        'regional-amounts-traded-pie-chart',
        `Info_Region_Monto_Transado_Nivel_Nacional_${yearNationalLevelValue}`
      ),
    downloadFileCallback: () =>
      exportToCsv(nationalLevel.csv, `Info_Region_Monto_Transado_Nivel_Nacional_${yearNationalLevelValue}`)
  })
  const {
    disableCaptureImage: disableCaptureProvidersSizeImage,
    disableDownloadFile: disableDownloadProvidersSizeFile,
    ref: providersSizeDataLayoutRef,
    onCaptureImage: handleCaptureProvidersSizeImage,
    onChangeView: handleChangeProvidersSizeView,
    onDownloadFile: handleDownloadProvidersSizeFile,
    update: updateProvidersSizeDataLayout
  } = useDataLayout({
    dependencies: [providersSize.csv, yearProvidersSizeValue],
    captureImageCallback: () =>
      exportToImage(
        'regional-amounts-traded-donuts-chart',
        `Info_Region_Monto_Transado_Tamano_Proveedor_${yearProvidersSizeValue}`
      ),
    downloadFileCallback: () =>
      exportToCsv(providersSize.csv, `Info_Region_Monto_Transado_Tamano_Proveedor_${yearProvidersSizeValue}`)
  })
  const handleChangeTab = useCallback(() => {
    updateNationalLevelDataLayout()
    updateProvidersSizeDataLayout()
  }, [])
  const handlePieChartTooltipFormatter = useCallback(
    function () {
      return `
        <ul>
          <li> - 
          <strong>
          ${currencyNationalLevelValue ? CURRENCY_VALUES.USD : CURRENCY_VALUES.CLP} ${formatAmount(this.point.y)}
      </strong> 
      transados en la <strong>${this.key}</strong>.</li>
          <br />
          <li>- Este corresponde al <strong>${formatPercentage(
            this.point?.percentage
          )}</strong> de su monto total transado durante el año.</li>
          <br />
          <li>- <strong>${this.point.orders}</strong> órdenes de compra.</li>
        </ul>
      `
    },
    [currencyNationalLevelValue]
  )
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const enhancedNationalLevelData = useMemo(
    () =>
      nationalLevel.data.map(datum => ({
        ...datum,
        color: theme.palette.brand[datum.colorName],
        y: currencyNationalLevelValue ? datum?.amountUSD : datum?.amount
      })),
    [currencyNationalLevelValue, nationalLevel.data, regionId]
  )
  const enhancedProvidersSizeData = useMemo(
    () =>
      providersSize.data.map(datum => ({
        ...datum,
        amount: currencyProvidersSizeValue ? datum?.amountUSD : datum?.amount,
        color: theme.palette.charts[datum.colorName]
      })),
    [currencyProvidersSizeValue, providersSize.data, regionId]
  )

  useEffect(() => {
    if (!endYear || !regionId) {
      return
    }

    batch(() => {
      dispatch(onGetRegionalNationalLevelThunk({ regionId, year: endYear }))
      dispatch(onGetRegionalProvidersSizeThunk({ regionId, year: endYear }))
    })
  }, [endYear, regionId])

  return (
    <Root>
      <GridContainer justifyContent="center" padding="0 0 23px">
        <GridItem xs={12}>
          <Title>Montos transados</Title>
        </GridItem>
      </GridContainer>
      <GridContainer justifyContent="center">
        <GridItem xs={12}>
          <Tabs value="national-level" onChange={handleChangeTab}>
            <Tab id="national-level" label="nivel nacional">
              <GridContainer padding="0 0 28px">
                <GridItem xs={12}>
                  <Description>
                    Conoce el monto total transado que acumuló la <Strong>{regionName}</Strong> según el año
                    seleccionado. Además, podrás comparar a cuanto equivale el monto regional en relación al total
                    nacional durante el año.
                  </Description>
                </GridItem>
              </GridContainer>
              <DataLayout
                disableCaptureImage={disableCaptureNationalLevelImage}
                disableDownloadFile={disableDownloadNationalLevelFile}
                header={
                  <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                    <GridItem md={2} xs={12}>
                      <Select
                        label="AÑO"
                        loading={loading}
                        options={yearsOptions}
                        value={yearNationalLevelValue}
                        onChange={handleChangeNationalLevelYear}
                      />
                    </GridItem>
                    <GridItem md={2} xs={12}>
                      <Switch
                        checked={currencyNationalLevelValue}
                        color="primary"
                        labelLeft="CLP"
                        labelRight="USD"
                        margin="0 0 0 32px"
                        onChange={handleChangeNationalLevelCurrency}
                      />
                    </GridItem>
                  </GridContainer>
                }
                infographic={<RegionalAmountsTradedNationalLevel />}
                noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
                ref={nationalLevelDataLayoutRef}
                updatedDate={latestUpdate}
                onCaptureImage={handleCaptureNationalLevelImage}
                onChangeView={handleChangeNationalLevelView}
                onDownloadFile={handleDownloadNationalLevelFile}
              >
                <DataLayoutChild view={DataLayoutViews.CHART}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <PieChart
                        data={enhancedNationalLevelData}
                        hoverColor={theme.palette.charts.darkRed}
                        id="regional-amounts-traded-pie-chart"
                        tooltipFormatter={handlePieChartTooltipFormatter}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem align="center" xs={12}>
                      <Total>
                        Total nacional:{' '}
                        <Strong>
                          {currencyNationalLevelValue
                            ? `${CURRENCY_VALUES.USD} ${formatAmount(nationalLevel.totalAmountUSD)}`
                            : `${CURRENCY_VALUES.CLP} ${formatAmount(nationalLevel.totalAmount)}`}
                        </Strong>
                      </Total>
                    </GridItem>
                  </GridContainer>
                  <GridContainer margin="30px 0 0">
                    <GridItem xs={12}>
                      <Info>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Info>
                    </GridItem>
                  </GridContainer>
                </DataLayoutChild>
                <DataLayoutChild view={DataLayoutViews.TABLE}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <DataTable
                        headers={[
                          { label: 'Año', value: 'year' },
                          { label: 'Región', value: 'name' },
                          { label: 'Monto transado', value: 'amount' },
                          { label: 'Monto transado (USD)', value: 'amountUSD' },
                          { label: 'N° de órdenes de compra', value: 'orders' }
                        ]}
                        headersCellsProps={[
                          { enableSort: true, width: '10%' },
                          { enableSort: true },
                          { enableSort: true, width: '20%' },
                          { enableSort: true, width: '20%' },
                          { enableSort: true, width: '20%' }
                        ]}
                        loading={loading}
                        records={enhancedNationalLevelData}
                        recordsDecorators={[
                          ({ year }) => year,
                          ({ name }) => name,
                          ({ amount }) => `$${formatAmount(amount)}`,
                          ({ amountUSD }) => `USD ${formatAmount(amountUSD)}`,
                          ({ orders }) => orders
                        ]}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer margin="30px 0 0">
                    <GridItem xs={12}>
                      <Info>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Info>
                    </GridItem>
                  </GridContainer>
                </DataLayoutChild>
              </DataLayout>
            </Tab>
            <Tab id="providers-size" label="tamaño de proveedores">
              <GridContainer padding="0 0 28px">
                <GridItem xs={12}>
                  <Description>
                    Conoce los tipos de empresa con los que la <Strong>{regionName}</Strong> ha transado según el año
                    seleccionado. Para obtener estas cifras se toman en cuenta todas las transacciones realizadas por
                    las <Strong>unidades de compra</Strong> pertenecientes a la región.
                  </Description>
                </GridItem>
              </GridContainer>
              <DataLayout
                disableCaptureImage={disableCaptureProvidersSizeImage}
                disableDownloadFile={disableDownloadProvidersSizeFile}
                header={
                  <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                    <GridItem md={2} xs={12}>
                      <Select
                        label="AÑO"
                        loading={loading}
                        options={yearsOptions}
                        value={yearProvidersSizeValue}
                        onChange={handleChangeProvidersSizeYear}
                      />
                    </GridItem>
                    <GridItem md={2} xs={12}>
                      <Switch
                        checked={currencyProvidersSizeValue}
                        color="primary"
                        labelLeft="CLP"
                        labelRight="USD"
                        onChange={handleChangeProvidersSizeCurrency}
                      />
                    </GridItem>
                  </GridContainer>
                }
                infographic={<RegionalAmountsTradedProvidersSize />}
                noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
                ref={providersSizeDataLayoutRef}
                updatedDate={latestUpdate}
                onCaptureImage={handleCaptureProvidersSizeImage}
                onChangeView={handleChangeProvidersSizeView}
                onDownloadFile={handleDownloadProvidersSizeFile}
              >
                <DataLayoutChild view={DataLayoutViews.CHART}>
                  <GridContainer margin="0 0 41px">
                    <GridItem xs={12}>
                      <Total>
                        Total regional:{' '}
                        {currencyProvidersSizeValue && (
                          <Strong>USD {formatAmount(providersSize.totalAmountUSD)}</Strong>
                        )}
                        {!currencyProvidersSizeValue && <Strong>${formatAmount(providersSize.totalAmount)}</Strong>}
                      </Total>
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12}>
                      <DonutsChart data={enhancedProvidersSizeData} id="regional-amounts-traded-donuts-chart" />
                    </GridItem>
                  </GridContainer>
                  <GridContainer margin="30px 0 0">
                    <GridItem xs={12}>
                      <Info>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Info>
                    </GridItem>
                  </GridContainer>
                </DataLayoutChild>
                <DataLayoutChild view={DataLayoutViews.TABLE}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <DataTable
                        headers={[
                          { label: 'Año', value: 'year' },
                          { label: 'Tamaño empresa proveedor', value: 'name' },
                          { label: 'Monto transado', value: 'amountCLP' },
                          { label: 'Monto transado (USD)', value: 'amountUSD' },
                          { label: 'N° de órdenes de compra', value: 'orders' }
                        ]}
                        headersCellsProps={[
                          { enableSort: true, width: '10%' },
                          { enableSort: true },
                          { enableSort: true, width: '20%' },
                          { enableSort: true, width: '20%' },
                          { enableSort: true, width: '20%' }
                        ]}
                        loading={loading}
                        records={enhancedProvidersSizeData}
                        recordsDecorators={[
                          ({ year }) => year,
                          ({ name }) => name,
                          ({ amount }) => `$${formatAmount(amount)}`,
                          ({ amountUSD }) => `$${formatAmount(amountUSD)}`,
                          ({ orders }) => orders
                        ]}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer margin="30px 0 0">
                    <GridItem xs={12}>
                      <Info>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Info>
                    </GridItem>
                  </GridContainer>
                </DataLayoutChild>
              </DataLayout>
            </Tab>
          </Tabs>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default RegionalAmountsTradedSection
